// Warnings for the person launching a product.
//
// Purely advisory. Nothing here blocks a launch, changes what is submitted, or
// alters a single wizard decision — the floor knows things the data does not,
// and a check that refuses work would be worse than no check at all. It exists
// so that putting a 1400 drum on a machine that takes 1250, or a length whose
// finished reel is over what the take-up is rated for, is noticed before it
// reaches the shop rather than after.
//
// Every warning names the FIELD it belongs to, so the wizard can put the icon
// against the control that is actually wrong — a mark on the row header only
// tells you something is off somewhere.
//
// Weight is NOT computed here. The wizard already works it out per phase (one
// conductor for insulation, the whole cable for sheathing) and shows it in the
// kg badge; recomputing it independently produced a number six times too large
// on a conductor-isolation run, sitting next to the correct one.

import { MACHINE_CAPABILITIES, machineById, LEVEL } from './machineCapabilities.js';
import { evaluateMachine } from './machineRouting.js';

export const SEVERITY = {
  // physically impossible or over a rated limit — someone will hit this on the floor
  BLOCKING: 'BLOCKING',
  // allowed, but not how this is normally done
  UNUSUAL: 'UNUSUAL',
};

/** Which control the warning belongs against. */
export const FIELD = {
  MACHINE: 'machine',
  DRUM: 'drum',
  LENGTH: 'length',
};

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(',', '.')); // European decimals
  return Number.isFinite(n) ? n : null;
};

// The wizards disagree about field names — the launch wizard holds
// machineId/plannedLength/drum_diameter, older forms hold machine/
// quantityPerDrum/drumDiameter. Read both rather than make every caller adapt.
const readRow = (r = {}) => ({
  machineId: String(r.machineId ?? r.machine ?? '').trim().toLowerCase(),
  lengthM: r.plannedLength ?? r.quantityPerDrum ?? r.length ?? null,
  drumDiameter: r.drum_diameter ?? r.drumDiameter ?? null,
  // A coil has no drum, and no take-up limit applies to it.
  isCoil: String(r.deliveryMode ?? '').toUpperCase() === 'COIL',
});

/**
 * What comes off the machine on THIS phase, in millimetres.
 *
 * Not the finished cable. An insulation run produces one insulated conductor —
 * for a CYYF 3x1.5 that is about 2.7 mm, where the finished cable is 10.2 — and
 * measuring the wrong one made L70 look as though it could not run a cable it
 * runs every day.
 *
 * Every phase carries the diameter of what it makes: insulation on each
 * conductor, everything else on the phase itself. Phases that only re-wind what
 * they are given (coiling, rewinding, unwinding) inherit from what feeds them.
 */
export function phaseOuterDiameter(phaseName, phases = [], conductor = null, depth = 0) {
  if (!phaseName || !Array.isArray(phases) || depth > 6) return null;
  const phase = phases.find((p) => p?.name === phaseName);
  if (!phase) return null;

  if (phaseName === 'conductor_isolation') {
    // per conductor — the row being checked belongs to exactly one of them
    const own = num(conductor?.isolation?.diameter);
    if (own) return own;
    // flat FY/MYF structure keeps the isolation on the phase
    const flat = num(phase.isolation?.diameter);
    if (flat) return flat;
    return num(phase.conductors?.[0]?.isolation?.diameter);
  }

  if (phaseName === 'pre_stranding') return num(phase.rope?.diameter);

  const own = num(phase.diameter);
  if (own) return own;

  // coiling / rewinding / unwinding make nothing new — ask what feeds them
  return phase.dependsOn
    ? phaseOuterDiameter(phase.dependsOn, phases, conductor, depth + 1)
    : null;
}

// Which control a routing complaint belongs against.
const fieldForReason = (reason) => {
  if (/drum Ø|take-up limit of \d+ mm|winds coils, not drums/.test(reason)) return FIELD.DRUM;
  return FIELD.MACHINE;
};

/**
 * Check one planned execution.
 *
 * @param row {{ machineId, plannedLength, drum_diameter, deliveryMode }}
 * @param ctx {{
 *   phase, section, conductors, productName, phases,
 *   weightKg,               // from the wizard's own per-phase calculation
 *   incomingDrumDiameter,   // drum arriving from the previous phase, if any
 *   incomingLabel
 * }}
 * @returns array of { field, severity, message }
 */
export function checkExecution(row, ctx) {
  const out = [];
  const r = readRow(row || {});
  const c = ctx || {};
  const machineId = r.machineId;
  if (!machineId) return out; // nothing chosen yet — the wizard already handles that

  const machine = machineById(machineId);
  if (!machine) {
    out.push({
      field: FIELD.MACHINE,
      severity: SEVERITY.UNUSUAL,
      message: `No capability data recorded for ${machineId.toUpperCase()} — nothing could be checked.`,
    });
    return out;
  }

  const label = machine.name || machineId.toUpperCase();
  const drumDiameter = r.isCoil ? null : num(r.drumDiameter);
  const phases = Array.isArray(c.phases) ? c.phases : [];
  // what this phase produces, not the finished cable
  const outerDiameter = phaseOuterDiameter(c.phase, phases, c.conductor);
  const weightKg = num(c.weightKg);

  // Until the row knows which phase it is, there is nothing to route.
  const verdict = c.phase
    ? evaluateMachine(machine, {
        phase: c.phase,
        section: num(c.section),
        conductors: num(c.conductors),
        outerDiameter,
        productKey: c.productName || null,
        drumDiameter,
        // weight is handled below, with the wizard's own figure
        drumWeightKg: null,
      })
    : { ok: false, level: null, reasons: [], unchecked: [], note: null };

  for (const reason of verdict.reasons) {
    out.push({
      field: fieldForReason(reason),
      severity: SEVERITY.BLOCKING,
      // the engine's reasons already carry the numbers; they only need the
      // machine's name in front
      message: `${label}: ${reason}.`,
    });
  }

  // Running on a machine that merely tolerates this work.
  if (verdict.ok && verdict.level === LEVEL.FALLBACK) {
    const better = MACHINE_CAPABILITIES.filter((m) => {
      if (m.machineId === machineId) return false;
      const v = evaluateMachine(m, {
        phase: c.phase,
        section: num(c.section),
        conductors: num(c.conductors),
        productKey: c.productName || null,
      });
      return v.ok && v.level !== LEVEL.FALLBACK;
    }).map((m) => m.name);

    out.push({
      field: FIELD.MACHINE,
      severity: SEVERITY.UNUSUAL,
      message:
        `${label} can do this, but it is not what the line is for` +
        (better.length ? ` — ${better.join(' or ')} normally does it.` : '.'),
    });
  }

  // The drum arriving from the previous phase rides the pay-off. A machine that
  // cannot accept it will not run, however well the output drum fits.
  const incoming = num(c.incomingDrumDiameter);
  const payoffMax = num(machine.payoff?.maxDrumDiameter);
  if (incoming && payoffMax && incoming > payoffMax) {
    out.push({
      field: FIELD.MACHINE,
      severity: SEVERITY.BLOCKING,
      message:
        `${label} does not support this input: the incoming drum is Ø${incoming} mm ` +
        `and the pay-off holds Ø${payoffMax} mm` +
        (c.incomingLabel ? ` (from ${c.incomingLabel}).` : '.'),
    });
  }

  // A closed list of drum sizes is not a ceiling. DTB1250 holds Ø1250 and
  // nothing else — a Ø1000 drum is under the maximum and still will not seat,
  // so checking only `maxDrumDiameter` let every smaller drum through in
  // silence, which is exactly the mistake this is meant to catch.
  const allowed = machine.takeUp?.drumDiameters;
  if (!r.isCoil && drumDiameter && Array.isArray(allowed) && allowed.length) {
    if (!allowed.includes(drumDiameter)) {
      // The closed list SUPERSEDES the maximum: a Ø1400 drum on DTB1250 is both
      // over the limit and not on the list, and saying so twice next to one
      // field reads as two separate problems.
      for (let i = out.length - 1; i >= 0; i -= 1) {
        if (
          out[i].field === FIELD.DRUM &&
          /over the take-up limit of \d+ mm/.test(out[i].message)
        ) {
          out.splice(i, 1);
        }
      }

      out.push({
        field: FIELD.DRUM,
        severity: SEVERITY.BLOCKING,
        message:
          `${label} takes ${allowed.map((d) => `Ø${d}`).join(' or ')} mm drums only — ` +
          `this run is set to Ø${drumDiameter} mm.`,
      });
    }
  }

  // Weight belongs against the length, because length is the field to change.
  const takeUpMax = num(machine.takeUp?.maxWeightKg);
  const lengthM = num(r.lengthM);
  if (!r.isCoil && weightKg && takeUpMax && weightKg > takeUpMax) {
    const fits = lengthM ? Math.floor((takeUpMax / weightKg) * lengthM) : null;
    out.push({
      field: FIELD.LENGTH,
      severity: SEVERITY.BLOCKING,
      message:
        `${Math.round(weightKg)} kg on the drum — ${label} take-up is rated for ${takeUpMax} kg.` +
        (fits ? ` Around ${fits} m would fit.` : ''),
    });
  }

  return out;
}

/** Group warnings by the control they belong against. */
export function byField(warnings = []) {
  const out = { [FIELD.MACHINE]: [], [FIELD.DRUM]: [], [FIELD.LENGTH]: [] };
  for (const w of warnings) {
    (out[w.field] || (out[w.field] = [])).push(w);
  }
  return out;
}

/** Worst severity across a set of warnings, or null when there are none. */
export function worstSeverity(warnings = []) {
  if (!warnings.length) return null;
  return warnings.some((w) => w.severity === SEVERITY.BLOCKING) ? SEVERITY.BLOCKING : SEVERITY.UNUSUAL;
}

/** Check several rows at once; returns the same array shape, flattened. */
export function checkExecutions(rows = [], ctx = {}) {
  return rows.flatMap((row) => checkExecution(row, ctx));
}
